import { type MouseEvent, useEffect, useId, useMemo, useState } from 'react'

import type { ModelRecord } from '../data/modelRegistry'
import type { ModelTier, ProviderRecommendation } from '../domain/types'
import { modelMeetsRouteFloor } from '../engine/modelFit'
import { ModelSearch, type ModelSearchMotionInput } from './ModelSearch'
import { ProviderIcon } from './ProviderIcon'
import {
  PINNED_MODEL_STORAGE_KEY,
  PINNED_PROVIDER_STORAGE_KEY,
  modelForProvider,
  modelsForRecommendations,
  readPinnedPreference,
  sameModel,
  uniqueModels,
  writePinnedModel,
  writePinnedProvider,
} from './modelPreference'

export { PINNED_MODEL_STORAGE_KEY, PINNED_PROVIDER_STORAGE_KEY }

const SEARCH_PANEL_CLEANUP_MS = 240

function routeLabel(tier: ModelTier): string {
  if (tier === 'direct') return 'Direct'
  if (tier === 'standard') return 'Standard'
  return 'Deep'
}

function compactModelName(name: string): string {
  return name
    .replace(/^GPT-5\.6\s+/i, '')
    .replace(/^Claude\s+/i, '')
    .replace(/^Gemini\s+/i, '')
    .replace(/^Gemma\s+/i, '')
    .replace(/^Qwen/i, '')
    .replace(/^Mistral\s+/i, '')
    .replace(/^DeepSeek\s+/i, '')
    .replace(/^Llama\s+/i, '')
}

function ModelSuggestion({
  model,
  routeTier,
  pinned,
  onTogglePin,
}: {
  model: ModelRecord
  routeTier: ModelTier
  pinned: boolean
  onTogglePin: (model: ModelRecord) => void
}) {
  const fits = modelMeetsRouteFloor(model.tier, routeTier)

  return (
    <li>
      <button
        type="button"
        className={`model-suggestion model-suggestion--${fits ? 'yes' : 'no'}`}
        aria-pressed={pinned}
        aria-label={`${fits ? 'Yes' : 'No'} for this ${routeLabel(
          routeTier,
        )} route. ${pinned ? 'Unpin' : 'Pin'} ${model.name} from ${
          model.provider
        }.`}
        onClick={() => onTogglePin(model)}
      >
        <span className="model-suggestion__fit" aria-hidden="true">
          {fits ? '✓' : 'No'}
        </span>
        <ProviderIcon provider={model.provider} modelName={model.name} />
        <span className="model-suggestion__name model-suggestion__name--full">
          {model.name}
        </span>
        <span
          className="model-suggestion__name model-suggestion__name--compact"
          aria-hidden="true"
        >
          {compactModelName(model.name)}
        </span>
        <span className="model-suggestion__pin" aria-hidden="true">
          {pinned ? '★' : '☆'}
        </span>
      </button>
    </li>
  )
}

export function ModelPicker({
  recommendations,
  routeTier,
}: {
  recommendations: ProviderRecommendation[]
  routeTier: ModelTier
}) {
  const [preference, setPreference] = useState(readPinnedPreference)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchPanelMounted, setSearchPanelMounted] = useState(false)
  const [searchMotionInput, setSearchMotionInput] =
    useState<ModelSearchMotionInput>('keyboard')
  const [announcement, setAnnouncement] = useState('')
  const searchPanelId = useId()
  const defaultModels = useMemo(
    () => modelsForRecommendations(recommendations),
    [recommendations],
  )
  const visibleModels = useMemo(() => {
    const preferredProviderModel = modelForProvider(
      preference.pinnedProvider,
      routeTier,
    )

    return uniqueModels([
      ...(preference.pinnedModel ? [preference.pinnedModel] : []),
      ...(preferredProviderModel ? [preferredProviderModel] : []),
      ...defaultModels,
    ]).slice(0, 3)
  }, [defaultModels, preference, routeTier])

  useEffect(() => {
    if (searchOpen || !searchPanelMounted || searchMotionInput !== 'pointer') {
      return
    }

    const cleanupTimer = window.setTimeout(() => {
      setSearchPanelMounted(false)
    }, SEARCH_PANEL_CLEANUP_MS)

    return () => window.clearTimeout(cleanupTimer)
  }, [searchMotionInput, searchOpen, searchPanelMounted])

  function updatePinnedModel(model: ModelRecord) {
    const nextModel = sameModel(preference.pinnedModel, model) ? null : model
    setPreference({
      pinnedModel: nextModel,
      pinnedProvider: null,
    })
    const persisted = writePinnedModel(nextModel)
    const message = nextModel
      ? `${model.name} pinned. It will appear first.`
      : `${model.name} unpinned.`
    setAnnouncement(
      persisted
        ? message
        : `${message} Browser storage is unavailable, so this change lasts only for this visit.`,
    )
  }

  function updatePinnedProvider(provider: string) {
    const nextProvider =
      preference.pinnedProvider === provider ? null : provider
    setPreference({
      pinnedModel: null,
      pinnedProvider: nextProvider,
    })
    const persisted = writePinnedProvider(nextProvider)
    const message = nextProvider
      ? `${provider} preferred. Its closest model will appear first.`
      : `${provider} provider preference removed.`
    setAnnouncement(
      persisted
        ? message
        : `${message} Browser storage is unavailable, so this change lasts only for this visit.`,
    )
  }

  function toggleSearch(event: MouseEvent<HTMLButtonElement>) {
    const motionInput = event.detail === 0 ? 'keyboard' : 'pointer'
    setSearchMotionInput(motionInput)

    if (!searchOpen) {
      setSearchPanelMounted(true)
      setSearchOpen(true)
      return
    }

    setSearchOpen(false)
    event.currentTarget.focus({ preventScroll: true })
    if (motionInput === 'keyboard') {
      setSearchPanelMounted(false)
    }
  }

  return (
    <section
      className="model-picker result-action"
      aria-labelledby={`${searchPanelId}-title`}
    >
      <span className="result-action__icon" aria-hidden="true">
        →
      </span>
      <div className="model-picker__content">
        <div className="model-picker__summary">
          <strong id={`${searchPanelId}-title`}>Try</strong>
          <ul className="model-suggestions" aria-label="Suggested models">
            {visibleModels.map((model) => (
              <ModelSuggestion
                key={`${model.provider}:${model.modelId}`}
                model={model}
                routeTier={routeTier}
                pinned={sameModel(preference.pinnedModel, model)}
                onTogglePin={updatePinnedModel}
              />
            ))}
          </ul>
          <button
            type="button"
            className="model-search-toggle"
            aria-expanded={searchOpen}
            aria-controls={searchPanelId}
            onClick={toggleSearch}
          >
            <span aria-hidden="true">⌕</span>
            <span>{searchOpen ? 'Close' : 'Find model'}</span>
          </button>
        </div>

        {searchPanelMounted ? (
          <ModelSearch
            id={searchPanelId}
            open={searchOpen}
            motionInput={searchMotionInput}
            routeTier={routeTier}
            pinnedModel={preference.pinnedModel}
            pinnedProvider={preference.pinnedProvider}
            onTogglePin={updatePinnedModel}
            onToggleProvider={updatePinnedProvider}
            onTransitionEnd={(event) => {
              if (
                event.target === event.currentTarget &&
                event.propertyName === 'opacity' &&
                !searchOpen
              ) {
                setSearchPanelMounted(false)
              }
            }}
          />
        ) : null}

        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </div>
    </section>
  )
}
